// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { useTranslation } from 'react-i18next';

import './PasswordStrengthMessage.less';
import { RegexPatterns } from '@/api/constants';
import type { PasswordPolicyUpdateRequest } from '@/api/types';

const TRANSLATION_ROOT = 'self-service.auth-method.password';
const alphabeticRequired = new RegExp(RegexPatterns.REQUIRE_ALPHABETIC);
const numericRequired = new RegExp(RegexPatterns.REQUIRE_NUMERIC);
const specialRequired = new RegExp(RegexPatterns.REQUIRE_SPECIAL);
const lowercaseRequired = new RegExp(RegexPatterns.REQUIRE_LOWERCASE);
const uppercaseRequired = new RegExp(RegexPatterns.REQUIRE_UPPERCASE);


interface PasswordStrengthMessageProps {
  passwordPolicy: PasswordPolicyUpdateRequest;
  currentPassword: string;
}

// Check which requirements are met based on the current password and the provided password policy
const isRequirementMet = (currentPassword: string, passwordPolicy: PasswordPolicyUpdateRequest) => ({
  length: currentPassword.length >= passwordPolicy.minimumLength && currentPassword.length <= passwordPolicy.maximumLength,
  alphabetic: passwordPolicy.requireAlphabetic ? alphabeticRequired.test(currentPassword) : true,
  lowercase: passwordPolicy.requireLowercase ? lowercaseRequired.test(currentPassword) : true,
  uppercase: passwordPolicy.requireUppercase ? uppercaseRequired.test(currentPassword) : true,
  numeric: passwordPolicy.requireNumeric ? numericRequired.test(currentPassword) : true,
  special: passwordPolicy.requireSpecial ? specialRequired.test(currentPassword) : true,
});

const displayPasswordRequirement = (requirementMet: boolean, requirementText: string) => {
  const requirementClass = requirementMet ? 'requirement-is-met' : 'requirement-is-not-met';

  return (
    <li className={requirementClass}>
      <i className="icon-check-circle"></i>
      {requirementText}
    </li>
  );
};

const PasswordStrengthMessage = ({ passwordPolicy, currentPassword }: PasswordStrengthMessageProps) => {
  const {t} = useTranslation();
  const requirementsMet = isRequirementMet(currentPassword, passwordPolicy);

  return (
    <div className="password-requirements">
      <div>{t(`${TRANSLATION_ROOT}.requirements-label`)}</div>
      <ul>
        {displayPasswordRequirement(
          currentPassword.length >= passwordPolicy.minimumLength && currentPassword.length <= passwordPolicy.maximumLength,
          t(`${TRANSLATION_ROOT}.password-length`, {
            minimumLength: passwordPolicy.minimumLength,
            maximumLength: passwordPolicy.maximumLength,
          })
        )}
        {passwordPolicy.requireAlphabetic && displayPasswordRequirement(
          requirementsMet.alphabetic,
          t(`${TRANSLATION_ROOT}.password-alphabetic-required`)
        )}
        {passwordPolicy.requireUppercase && displayPasswordRequirement(
          requirementsMet.uppercase,
          t(`${TRANSLATION_ROOT}.password-uppercase-required`)
        )}
        {passwordPolicy.requireLowercase && displayPasswordRequirement(
          requirementsMet.lowercase,
          t(`${TRANSLATION_ROOT}.password-lowercase-required`)
        )}
        {passwordPolicy.requireNumeric && displayPasswordRequirement(
          requirementsMet.numeric,
          t(`${TRANSLATION_ROOT}.password-numeric-required`)
        )}
        {passwordPolicy.requireSpecial && displayPasswordRequirement(
          requirementsMet.special,
          t(`${TRANSLATION_ROOT}.password-special-required`)
        )}
      </ul>
    </div>
  );
};

export default PasswordStrengthMessage;
